// frontend/src/hooks/commonEvents/useTimetableViewerPageElement.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DsModal from '../../components/common/DsModal';
import { useToast } from '../../components/common/ToastSystem';
import {
  ActionRowsSkeleton,
  TimetableViewerSkeleton,
} from '../../components/common/ContentSkeletons';
import { AvatarImage } from '../../components/common/avatar/AvatarImage';
import { formatDateTimeJST, toYmdSlash } from '../../utils/datetime';
import {
  applyRowAdjustments,
  buildAdjustmentMap,
  buildMemoMap,
  buildTimelineRows,
  formatStartTime,
  getRowDurationValue,
  getRowItemText,
  getRowStartTime,
  normalizeTimetableTargetDate as normalizeTargetDate,
} from '../../utils/timetableTimeline';
import { getSocket } from '../../lib/socket';
import { SOCKET_EVENTS } from '../../lib/socketEvents';
import {
  createEventTimetableAnnouncement,
  createStagePreviewPublicLink,
  getEventDetail,
  getEventTimetableRuntimeState,
  getStageSeatingForViewer,
  listEventTimetables,
  listSharedEventTimetables,
  listStagePreviewPublicLinks,
  revokeStagePreviewPublicLink,
  upsertEventTimetableAdjustment,
  upsertEventTimetableMemo,
} from '../../api/taskEvents';
import '../../css/common/TimetableViewerPage.css';

const API_URL = process.env.REACT_APP_API_URL;
const ARTIST_PROFILE_IMAGE_PATH = (
  process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH || '/api/images/artist_profiles'
).replace(/\/$/, '');

const getDefaultColumnAlign = (id) => {
  if (id === 'startTime' || id === 'duration') return 'center';
  return 'left';
};

const DEFAULT_TITLE_STYLE = {
  bgColor: 'var(--tt-header-bg)',
  textColor: 'var(--tt-header-text)',
  fontSize: 14,
  bold: true,
  height: 36,
};

const DEFAULT_TIMETABLE_COLUMNS = [
  { id: 'startTime', label: '時刻', align: getDefaultColumnAlign('startTime') },
  { id: 'duration', label: '所要時間', align: getDefaultColumnAlign('duration') },
  { id: 'item', label: '進行・曲目', align: getDefaultColumnAlign('item') },
];

const SP_BREAKPOINT = 768;
const TIME_COLUMN_SIDE_PADDING_SP = 3;
const TIME_COLUMN_SIDE_PADDING_PC = 8;
const TIME_COLUMN_MIN_SP = 42;
const TIME_COLUMN_MIN_PC = 64;
const TIME_COLUMN_MAX_PC = 112;

const COLOR_MODE_STORAGE_KEY = 'timetable-viewer-color-mode';
const PROGRAM_ANIM_DURATION = 900;
const ANNOUNCEMENT_DISMISS_KEY_PREFIX = 'timetable-announcement-dismissed-';
const DEFAULT_CANVAS_ASPECT = '4:3';
const DEFAULT_CANVAS_BACKGROUND_COLOR = 'var(--gray-100)';
const DEFAULT_ICON_SIZE = 34;
const CANVAS_ASPECT_RATIOS = {
  '4:3': '4 / 3',
  '16:9': '16 / 9',
  '3:2': '3 / 2',
  '1:1': '1 / 1',
  '3:4': '3 / 4',
};

const clampPercent = (value, fallback = 50) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(100, Math.max(0, num));
};

const normalizeCanvasAspect = (value) => {
  const key = String(value || '').trim();
  if (CANVAS_ASPECT_RATIOS[key]) return key;
  const match = key.match(/^([0-9]+(?:\.[0-9]+)?):([0-9]+(?:\.[0-9]+)?)$/);
  if (match) {
    const width = Number(match[1]);
    const height = Number(match[2]);
    const ratio = width / height;
    if (Number.isFinite(ratio) && ratio >= 0.2 && ratio <= 8) return `${width}:${height}`;
  }
  return DEFAULT_CANVAS_ASPECT;
};

const getCanvasAspectRatioCss = (value) => {
  const normalized = normalizeCanvasAspect(value);
  return CANVAS_ASPECT_RATIOS[normalized] || normalized.replace(':', ' / ');
};

const normalizeCanvasBackgroundColor = (value) => {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^[0-9a-fA-F]{6}$/.test(raw)) return `#${raw.toLowerCase()}`;
  return DEFAULT_CANVAS_BACKGROUND_COLOR;
};

const normalizeIconSize = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return DEFAULT_ICON_SIZE;
  return Math.min(72, Math.max(18, Math.round(num)));
};

const normalizeShapeSize = (value, fallback) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.min(100, Math.max(1, num));
};

const normalizeRotation = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return ((num % 360) + 360) % 360;
};

const normalizeOpacity = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 1;
  return Math.min(1, Math.max(0.05, num));
};

const buildProfileImageUrl = (profilePicture) => {
  const key = String(profilePicture || '').trim();
  if (!key) return '';
  const base = String(API_URL || '').replace(/\/$/, '');
  return `${base}${ARTIST_PROFILE_IMAGE_PATH}/${encodeURIComponent(key)}?variant=proxy`;
};

function TimetableStandaloneModal({ open, onClose, title, colorMode = 'light', className = '', children }) {
  const modeClassName = colorMode === 'dark' ? 'is-dark' : 'is-light';
  return (
    <DsModal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      className={`timetable-standalone-modal-card ${modeClassName}${className ? ` ${className}` : ''}`}
    >
      {children}
    </DsModal>
  );
}

const getInitialColorMode = () => {
  if (typeof window === 'undefined') return 'light';
  let saved = null;
  try {
    saved = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  } catch (err) {
    saved = null;
  }
  if (saved === 'light' || saved === 'dark') return saved;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
};

const getPublicLinkStatusLabel = (status) => {
  if (status === 'active') return '有効';
  if (status === 'expired') return '期限切れ';
  if (status === 'revoked') return '失効済み';
  return '不明';
};

const formatPublicLinkDate = (value) => {
  if (!value) return 'なし';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'なし';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const buildPreviewColumns = (data = {}) => {
  const cols = Array.isArray(data.columns) && data.columns.length ? data.columns : DEFAULT_TIMETABLE_COLUMNS;
  return cols.map((c) => ({
    ...c,
    align: c.align || getDefaultColumnAlign(c.id),
  }));
};

const buildPreviewRows = (data = {}) => {
  if (Array.isArray(data.rows) && data.rows.length) return data.rows;
  return [
    {
      id: 'row-1',
      startTime: '',
      duration: '',
      item: '',
      rowType: 'normal',
      rowColor: '',
      rowTextColor: '',
      cells: {},
    },
  ];
};

const buildHeaderGroups = (columns = []) => {
  const groups = [];
  let idx = 0;
  while (idx < columns.length) {
    const base = columns[idx];
    const baseLabel = (base.label || '').trim();
    let span = 1;
    idx += 1;
    while (idx < columns.length && (columns[idx].label || '').trim() === baseLabel) {
      span += 1;
      idx += 1;
    }
    groups.push({
      label: baseLabel,
      span,
      align: base.align || getDefaultColumnAlign(base.id),
      startId: base.id,
    });
  }
  return groups;
};

const pickColumnWidths = (data = {}, columns = []) => {
  const raw = data.columnWidths && typeof data.columnWidths === 'object' ? data.columnWidths : {};
  const next = {};
  columns.forEach((c) => {
    if (c.id === 'startTime') return;
    const w = raw[c.id];
    if (typeof w === 'number' && Number.isFinite(w) && w > 0) {
      next[c.id] = w;
    }
  });
  return next;
};

const estimateTextWidth = (text, fontSize = 14, weight = 600) => {
  const value = String(text || '');
  if (!value) return 0;
  if (typeof document !== 'undefined') {
    const canvas = estimateTextWidth.canvas || document.createElement('canvas');
    estimateTextWidth.canvas = canvas;
    const context = canvas.getContext('2d');
    if (context) {
      context.font = `${weight} ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
      return context.measureText(value).width;
    }
  }
  return value.length * fontSize * 0.58;
};

const resolveTimetableViewportWidth = (containerWidth) => {
  if (Number.isFinite(containerWidth) && containerWidth > 0) return containerWidth;
  if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth)) return window.innerWidth;
  return 1024;
};

const estimateTimeColumnWidth = ({
  rows = [],
  label = '時刻',
  hasDurationColumn = false,
  titleStyle = DEFAULT_TITLE_STYLE,
  containerWidth = 0,
} = {}) => {
  const viewportWidth = resolveTimetableViewportWidth(containerWidth);
  const isCompact = viewportWidth <= SP_BREAKPOINT;
  const sidePadding = isCompact ? TIME_COLUMN_SIDE_PADDING_SP : TIME_COLUMN_SIDE_PADDING_PC;
  const minWidth = isCompact ? TIME_COLUMN_MIN_SP : TIME_COLUMN_MIN_PC;
  const maxWidth = isCompact
    ? Math.max(minWidth, Math.floor(viewportWidth / 4))
    : TIME_COLUMN_MAX_PC;

  let contentWidth = estimateTextWidth(label || '時刻', titleStyle.fontSize || DEFAULT_TITLE_STYLE.fontSize, 700);
  rows.forEach((row) => {
    const baseStart = getRowStartTime(row);
    const hasAdjustedTime =
      row.adjustedOffsetMinutes && row.adjustedStartTime && String(baseStart || '').trim();
    const displayStart = hasAdjustedTime ? row.adjustedStartTime : baseStart || '--:--';
    const timeText = hasAdjustedTime ? `${formatStartTime(baseStart)} → ${formatStartTime(displayStart)}` : formatStartTime(displayStart);
    contentWidth = Math.max(contentWidth, estimateTextWidth(timeText || '--:--', 14, 700));
    if (hasDurationColumn) {
      const durationText = getRowDurationValue(row);
      if (durationText !== null && durationText !== undefined && String(durationText).trim()) {
        contentWidth = Math.max(contentWidth, estimateTextWidth(`(${String(durationText).trim()})`, 12, 600));
      }
    }
  });

  const desiredWidth = Math.ceil(contentWidth + sidePadding * 2);
  return Math.max(minWidth, Math.min(maxWidth, desiredWidth));
};

const pickRowHeights = (data = {}, rows = []) => {
  const raw = data.rowHeights && typeof data.rowHeights === 'object' ? data.rowHeights : {};
  const next = {};
  rows.forEach((r) => {
    const h = raw[r.id];
    if (typeof h === 'number' && Number.isFinite(h) && h > 0) {
      next[r.id] = h;
    }
  });
  return next;
};

const normalizeTimetableLink = (value = {}) => {
  const raw = value && typeof value === 'object' ? value : {};
  const timetableValue = Number(raw.timetable_id ?? raw.timetableId);
  const timetableId = Number.isSafeInteger(timetableValue) && timetableValue > 0 ? timetableValue : null;
  const rowSource = Array.isArray(raw.row_ids)
    ? raw.row_ids
    : (Array.isArray(raw.rowIds) ? raw.rowIds : []);
  return {
    timetable_id: timetableId,
    row_ids: timetableId
      ? Array.from(new Set(rowSource.map((item) => String(item || '').trim()).filter(Boolean)))
      : [],
  };
};

const normalizeSeatingLayoutData = (layoutData = {}) => {
  const sourceCanvas = layoutData.canvas || {};
  const customIcons = Array.isArray(layoutData.custom_icons) ? layoutData.custom_icons : [];
  const customIds = new Set(customIcons.map((item) => String(item.id)));
  return {
    canvas: {
      aspect_ratio: normalizeCanvasAspect(sourceCanvas.aspect_ratio || sourceCanvas.aspectRatio),
      background_color: normalizeCanvasBackgroundColor(sourceCanvas.background_color || sourceCanvas.backgroundColor),
    },
    icon_size: normalizeIconSize(layoutData.icon_size || layoutData.iconSize),
    background: layoutData.background || null,
    placements: (Array.isArray(layoutData.placements) ? layoutData.placements : [])
      .map((item) => {
        const artistId = String(item?.artist_id || item?.artistId || '').trim();
        if (!artistId) return null;
        return { artist_id: artistId, x: clampPercent(item.x), y: clampPercent(item.y) };
      })
      .filter(Boolean),
    custom_icons: customIcons.map((item) => ({
      id: String(item.id || ''),
      label: String(item.label || '').slice(0, 120),
      role: String(item.role || item.note || '').slice(0, 120),
      icon_text: String(item.icon_text || item.iconText || item.label || '?').slice(0, 4),
      color: item.color || 'var(--purple-600)',
    })),
    custom_placements: (Array.isArray(layoutData.custom_placements) ? layoutData.custom_placements : [])
      .map((item) => {
        const customId = String(item.custom_id || item.customId || '').trim();
        if (!customId || !customIds.has(customId)) return null;
        return { custom_id: customId, x: clampPercent(item.x), y: clampPercent(item.y) };
      })
      .filter(Boolean),
    text_items: (Array.isArray(layoutData.text_items) ? layoutData.text_items : [])
      .map((item) => ({
        id: String(item.id || ''),
        text: String(item.text || '').slice(0, 300),
        x: clampPercent(item.x),
        y: clampPercent(item.y),
        width: normalizeShapeSize(item.width, 18),
        height: normalizeShapeSize(item.height, 6),
        size: Math.min(96, Math.max(8, Number(item.size) || 14)),
        weight: Number(item.weight || item.font_weight || item.fontWeight) >= 700 ? 700 : 500,
        align: ['left', 'center', 'right'].includes(item.align) ? item.align : 'center',
        color: item.color || 'var(--slate-900)',
        opacity: normalizeOpacity(item.opacity),
        rotation: normalizeRotation(item.rotation),
        z_index: Number.isFinite(Number(item.z_index ?? item.zIndex)) ? Number(item.z_index ?? item.zIndex) : 0,
      }))
      .filter((item) => item.text),
    shapes: (Array.isArray(layoutData.shapes) ? layoutData.shapes : [])
      .map((item) => ({
        id: String(item.id || ''),
        type: String(item.type || 'square'),
        label: String(item.label || '').slice(0, 80),
        x: clampPercent(item.x),
        y: clampPercent(item.y),
        width: normalizeShapeSize(item.width, item.type === 'line' ? 22 : 12),
        height: normalizeShapeSize(item.height, item.type === 'line' ? 2 : 12),
        rotation: normalizeRotation(item.rotation),
        fill: item.fill || 'rgba(148, 163, 184, 0.35)',
        stroke: item.stroke || 'var(--slate-500)',
        opacity: normalizeOpacity(item.opacity),
        z_index: Number.isFinite(Number(item.z_index ?? item.zIndex)) ? Number(item.z_index ?? item.zIndex) : 0,
      })),
    timetable: normalizeTimetableLink(layoutData.timetable),
  };
};

const buildProgramView = (table, nowRowId, adjustmentsMap) => {
  if (!table) return { current: null, next: null, next2: null };
  const ymd = normalizeTargetDate(table?.target_date);
  if (!ymd) return { current: null, next: null, next2: null };
  const data = table.data || {};
  const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
  const timeline = buildTimelineRows(rows, ymd);

  if (!timeline.length) return { current: null, next: null, next2: null };

  const nowMs = Date.now();
  const findById = (id) => timeline.find((t) => String(t.id) === String(id)) || null;
  let current = nowRowId ? findById(nowRowId) : null;
  if (!current) {
    current = timeline.find((t) => t.startMs <= nowMs && nowMs < t.endMs) || null;
  }

  let next = null;
  let next2 = null;
  if (current) {
    const idx = timeline.findIndex((t) => String(t.id) === String(current.id));
    if (idx >= 0) {
      next = timeline[idx + 1] || null;
      next2 = timeline[idx + 2] || null;
    }
  } else {
    next = timeline.find((t) => t.startMs > nowMs) || null;
    if (next) {
      const idx = timeline.findIndex((t) => String(t.id) === String(next.id));
      next2 = timeline[idx + 1] || null;
    }
  }

  return { current, next, next2 };
};

const normalizeProgramView = (view) => {
  if (!view) return { current: null, next: null };
  if (!view.current && view.next) {
    return { current: view.next, next: view.next2 || null };
  }
  return { current: view.current || null, next: view.next || null };
};

const getProgramDisplaySignature = (item) => {
  if (!item) return '';
  return [
    item.id,
    item.item,
    item.startTime,
    item.rawStartTime,
    item.adjustedOffsetMinutes,
    item.hasAdjustedTime ? 'adjusted' : 'plain',
  ].join('\u0001');
};

const isSameProgramDisplayItem = (a, b) => getProgramDisplaySignature(a) === getProgramDisplaySignature(b);

const renderProgramStartTime = (item) => {
  if (!item?.startTime) return null;
  if (!item.hasAdjustedTime) {
    return <div className="program-time">{item.startTime}</div>;
  }
  const mode = item.adjustedOffsetMinutes > 0 ? 'is-push' : 'is-pull';
  const offsetLabel = `${item.adjustedOffsetMinutes > 0 ? '+' : ''}${item.adjustedOffsetMinutes}分`;
  return (
    <div className={`program-time program-time--adjusted ${mode}`}>
      <span className="program-time-original">{item.rawStartTime}</span>
      <span className="program-time-arrow">→</span>
      <span className="program-time-new">{item.startTime}</span>
      <span className="program-time-offset">{offsetLabel}</span>
    </div>
  );
};

const findNowRowId = (table, adjustmentsMap) => {
  const ymd = normalizeTargetDate(table?.target_date);
  if (!ymd) return null;
  const data = table.data || {};
  const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
  const timeline = buildTimelineRows(rows, ymd);
  const nowMs = Date.now();
  let candidate = null;
  timeline.forEach((r) => {
    if (r.startMs <= nowMs && nowMs < r.endMs) {
      if (!candidate || candidate.startMs < r.startMs) {
        candidate = { id: r.id, startMs: r.startMs };
      }
    }
  });
  return candidate?.id || null;
};

const findNextStartMs = (table, adjustmentsMap) => {
  const ymd = normalizeTargetDate(table?.target_date);
  if (!ymd) return null;
  const data = table.data || {};
  const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
  const timeline = buildTimelineRows(rows, ymd);
  const nowMs = Date.now();
  let next = null;
  timeline.forEach((r) => {
    if (r.startMs > nowMs && (next === null || r.startMs < next)) {
      next = r.startMs;
    }
  });
  return next;
};

const pickTitleStyle = (data = {}) => ({
  ...DEFAULT_TITLE_STYLE,
  ...(data.titleStyle || {}),
});

const buildEventLabel = (source) => {
  if (!source) return '';
  const name = String(source.event_name ?? source.eventName ?? source.name ?? source.title ?? '').trim();
  if (String(source.event_mode ?? source.eventMode ?? '').trim() === 'multi_performance') {
    return name;
  }
  const performance = String(
    source.performance_type ?? source.performanceType ?? source.performance_name ?? source.performanceName ?? ''
  ).trim();
  return [name, performance].filter(Boolean).join(' ');
};

const buildTimetablePerformanceLabel = (source) => {
  if (!source?.event_performance_id) return '';
  return String(source.performance_name ?? source.performanceName ?? '').trim();
};

const scaleColumnWidths = (columnWidths, columns, containerWidth) => {
  if (!containerWidth) return columnWidths;
  if (!columns.length) return columnWidths;
  const fixedIds = new Set();
  const startTimeWidth = columnWidths.startTime;
  if (typeof startTimeWidth === 'number' && startTimeWidth > 0) {
    fixedIds.add('startTime');
  }
  const durationWidth = columnWidths.duration;
  if (typeof durationWidth === 'number' && durationWidth > 0) {
    fixedIds.add('duration');
  }
  const values = columns
    .map((c) => ({
      id: c.id,
      width: columnWidths[c.id],
    }))
    .filter((entry) => typeof entry.width === 'number' && entry.width > 0);
  const total = values.reduce((sum, entry) => sum + entry.width, 0);
  if (!total) {
    const base = Math.max(60, Math.floor(containerWidth / columns.length));
    const next = {};
    columns.forEach((c) => {
      next[c.id] = base;
    });
    return next;
  }
  if (total >= containerWidth) return columnWidths;
  const fixedTotal = values
    .filter((entry) => fixedIds.has(entry.id))
    .reduce((sum, entry) => sum + entry.width, 0);
  const scalable = values.filter((entry) => !fixedIds.has(entry.id));
  const scalableTotal = scalable.reduce((sum, entry) => sum + entry.width, 0);
  if (!scalableTotal) return columnWidths;
  const remaining = Math.max(containerWidth - fixedTotal, 0);
  const scale = remaining / scalableTotal;
  const next = {};
  columns.forEach((c) => {
    const w = columnWidths[c.id];
    if (typeof w === 'number' && w > 0) {
      if (fixedIds.has(c.id)) {
        next[c.id] = w;
      } else {
        next[c.id] = Math.max(60, Math.round(w * scale));
      }
    }
  });
  return next;
};

export default function useTimetableViewerPageElement({ mode = 'admin' }) {
  const { eventId, timetableId } = useParams();
  const [searchParams] = useSearchParams();
  const isConfirmationPreview = mode === 'admin' && searchParams.get('view') === 'preview';
  const { success: toastSuccess, error: toastError } = useToast();
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventLabel, setEventLabel] = useState('');
  const [nowLabel, setNowLabel] = useState('');
  const [nowTimetableRowId, setNowTimetableRowId] = useState(null);
  const [colorMode, setColorMode] = useState(getInitialColorMode);
  const [isModeOpen, setIsModeOpen] = useState(false);
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [isClockFullscreen, setIsClockFullscreen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [programDisplay, setProgramDisplay] = useState({ current: null, next: null });
  const [isProgramAnimating, setIsProgramAnimating] = useState(false);
  const [currentTitleFontSize, setCurrentTitleFontSize] = useState(null);
  const [runtimeData, setRuntimeData] = useState({
    adjustments: [],
    memos: [],
    announcements: [],
    permissions: { can_edit: false },
  });
  const [stageSeatingData, setStageSeatingData] = useState({
    layouts: [],
    participants: [],
    loading: false,
    error: '',
  });
  const [seatingPreview, setSeatingPreview] = useState({
    open: false,
    row: null,
    layoutId: null,
  });
  const [publicLinkModalOpen, setPublicLinkModalOpen] = useState(false);
  const [publicLinks, setPublicLinks] = useState([]);
  const [publicLinksLoading, setPublicLinksLoading] = useState(false);
  const [publicLinksError, setPublicLinksError] = useState('');
  const [publicLinkCreating, setPublicLinkCreating] = useState(false);
  const [publicLinkRevokingId, setPublicLinkRevokingId] = useState(null);
  const [publicLinkUrlsById, setPublicLinkUrlsById] = useState({});
  const [activeSeatingPreviewMemberKey, setActiveSeatingPreviewMemberKey] = useState('');
  const [, setRuntimeLoading] = useState(false);
  const [runtimeError, setRuntimeError] = useState('');
  const [runtimeForbidden, setRuntimeForbidden] = useState(false);
  const [adjustmentModal, setAdjustmentModal] = useState({
    open: false,
    row: null,
    mode: 'push',
    minutes: '5',
    error: '',
    saving: false,
  });
  const [memoModal, setMemoModal] = useState({
    open: false,
    row: null,
    content: '',
    visibility: 'all',
    sendToChat: false,
    error: '',
    saving: false,
  });
  const [announcementModal, setAnnouncementModal] = useState({
    open: false,
    content: '',
    duration: '5',
    visibility: 'all',
    sendToChat: true,
    error: '',
    saving: false,
  });
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  const [isAnnouncementFullscreen, setIsAnnouncementFullscreen] = useState(false);
  const highlightTimerRef = useRef(null);
  const programDisplayRef = useRef({ current: null, next: null });
  const programAnimTimerRef = useRef(null);
  const programTitleRef = useRef(null);
  const headerMenuRef = useRef(null);
  const tableWrapRef = useRef(null);
  const rowRefs = useRef(new Map());
  const prevNowRowIdRef = useRef(null);
  const [tableWrapWidth, setTableWrapWidth] = useState(0);
  const [tableHasHorizontalOverflow, setTableHasHorizontalOverflow] = useState(false);
  const [tableScrolledHorizontally, setTableScrolledHorizontally] = useState(false);
  const longPressTimerRef = useRef(null);

  const adjustmentsMap = useMemo(
    () => buildAdjustmentMap(runtimeData?.adjustments || []),
    [runtimeData?.adjustments],
  );
  const memoMap = useMemo(() => buildMemoMap(runtimeData?.memos || []), [runtimeData?.memos]);
  const participantByArtistId = useMemo(() => {
    const map = new Map();
    (stageSeatingData.participants || []).forEach((artist) => {
      const id = String(artist?.artist_id ?? artist?.id ?? '').trim();
      if (id) map.set(id, artist);
    });
    return map;
  }, [stageSeatingData.participants]);
  const linkedLayoutsByRowId = useMemo(() => {
    const map = new Map();
    if (!timetable?.id) return map;
    (stageSeatingData.layouts || []).forEach((layout) => {
      const data = normalizeSeatingLayoutData(layout?.layout_data || {});
      const link = normalizeTimetableLink(data.timetable || layout?.timetable);
      if (String(link.timetable_id || '') !== String(timetable.id)) return;
      link.row_ids.forEach((rowId) => {
        if (!rowId) return;
        if (!map.has(rowId)) map.set(rowId, []);
        map.get(rowId).push({ ...layout, normalized_layout_data: data });
      });
    });
    return map;
  }, [stageSeatingData.layouts, timetable?.id]);
  const canEditRuntime = !isConfirmationPreview && !!runtimeData?.permissions?.can_edit;
  const programCurrentItem = programDisplay?.current?.item || '';

  const adjustCurrentTitleSize = useCallback(() => {
    if (typeof window === 'undefined') return;
    const el = programTitleRef.current;
    const text = String(programCurrentItem || '').trim();
    if (!el || !text) {
      setCurrentTitleFontSize(null);
      return;
    }
    el.style.fontSize = '';
    const computed = window.getComputedStyle(el);
    const baseSize = parseFloat(computed.fontSize);
    const lineHeightValue = parseFloat(computed.lineHeight);
    if (!Number.isFinite(baseSize) || baseSize <= 0) {
      setCurrentTitleFontSize(null);
      return;
    }
    const lineHeight = Number.isFinite(lineHeightValue) && lineHeightValue > 0 ? lineHeightValue : baseSize * 1.2;
    const maxHeight = lineHeight * 2 + 0.5;
    let size = baseSize;
    const minSize = Math.max(16, Math.round(baseSize * 0.55));
    let iterations = 0;
    while (el.scrollHeight > maxHeight && size > minSize && iterations < 24) {
      size -= 1;
      el.style.fontSize = `${size}px`;
      iterations += 1;
    }
    if (size < baseSize) {
      setCurrentTitleFontSize(size);
      return;
    }
    setCurrentTitleFontSize(null);
  }, [programCurrentItem]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!isClockFullscreen) {
      setCurrentTitleFontSize(null);
      return undefined;
    }
    let rafId = null;
    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        adjustCurrentTitleSize();
      });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isClockFullscreen, adjustCurrentTitleSize]);

  const fetchTimetable = useCallback(async () => {
    if (!eventId || !timetableId) return;
    setLoading(true);
    setError('');
    try {
      const list =
        mode === 'artist'
          ? await listSharedEventTimetables(eventId)
          : await listEventTimetables(eventId);
      const found = list.find((t) => String(t.id) === String(timetableId)) || null;
      if (!found) {
        setError('指定のタイムテーブルが見つかりません。');
      }
      setTimetable(found);
    } catch (err) {
      setError(err.message || 'タイムテーブルの取得に失敗しました');
      setTimetable(null);
    } finally {
      setLoading(false);
    }
  }, [eventId, timetableId, mode]);

  const fetchRuntime = useCallback(async () => {
    if (!eventId || !timetableId) return;
    if (isConfirmationPreview) {
      setRuntimeData({ adjustments: [], memos: [], announcements: [], permissions: { can_edit: false } });
      setRuntimeError('');
      return;
    }
    if (runtimeForbidden) return;
    setRuntimeLoading(true);
    setRuntimeError('');
    try {
      const runtime = await getEventTimetableRuntimeState(eventId, timetableId);
      setRuntimeForbidden(false);
      setRuntimeData({
        adjustments: Array.isArray(runtime?.adjustments) ? runtime.adjustments : [],
        memos: Array.isArray(runtime?.memos) ? runtime.memos : [],
        announcements: Array.isArray(runtime?.announcements) ? runtime.announcements : [],
        permissions: runtime?.permissions || { can_edit: false },
      });
    } catch (err) {
      if (Number(err?.status) === 403) {
        if (mode === 'admin') {
          setRuntimeForbidden(true);
          setRuntimeData({
            adjustments: [],
            memos: [],
            announcements: [],
            permissions: { can_edit: false },
          });
          setRuntimeError('');
          return;
        }
        setRuntimeError('権限がありません。');
        return;
      }
      setRuntimeError(err.message || 'ランタイム情報の取得に失敗しました');
      setRuntimeData({
        adjustments: [],
        memos: [],
        announcements: [],
        permissions: { can_edit: false },
      });
    } finally {
      setRuntimeLoading(false);
    }
  }, [eventId, isConfirmationPreview, mode, runtimeForbidden, timetableId]);

  const fetchStageSeating = useCallback(async () => {
    if (!eventId) return;
    setStageSeatingData((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const viewerScope = mode === 'artist' ? 'artist' : 'admin';
      const json = await getStageSeatingForViewer(eventId, viewerScope);
      setStageSeatingData({
        layouts: Array.isArray(json?.layouts) ? json.layouts : [],
        participants: Array.isArray(json?.participants) ? json.participants : [],
        loading: false,
        error: '',
      });
    } catch (err) {
      setStageSeatingData({
        layouts: [],
        participants: [],
        loading: false,
        error: err.message || 'シーティング情報の取得に失敗しました',
      });
    }
  }, [eventId, mode]);

  const fetchEventInfo = useCallback(async () => {
    if (!eventId) return;
    try {
      const eventData = await getEventDetail(eventId);
      const label = buildEventLabel(eventData);
      setEventLabel(label);
    } catch (err) {
      setEventLabel('');
    }
  }, [eventId]);

  useEffect(() => {
    fetchTimetable();
  }, [fetchTimetable]);

  useEffect(() => {
    setRuntimeForbidden(false);
  }, [eventId, timetableId]);

  useEffect(() => {
    fetchRuntime();
  }, [fetchRuntime]);

  useEffect(() => {
    fetchStageSeating();
  }, [fetchStageSeating]);

  useEffect(() => {
    fetchEventInfo();
  }, [fetchEventInfo]);

  useEffect(() => {
    const tick = () => {
      setNowLabel(formatDateTimeJST(new Date().toISOString()));
    };
    tick();
    const timer = setInterval(tick, 15_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorMode);
    } catch (err) {
      // ignore storage errors
    }
  }, [colorMode]);

  const isAnnouncementDismissed = useCallback((id) => {
    if (!id || typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(`${ANNOUNCEMENT_DISMISS_KEY_PREFIX}${id}`) === '1';
    } catch (err) {
      return false;
    }
  }, []);

  const markAnnouncementDismissed = useCallback((id) => {
    if (!id || typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(`${ANNOUNCEMENT_DISMISS_KEY_PREFIX}${id}`, '1');
    } catch (err) {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setSeatingPreview({ open: false, row: null, layoutId: null });
      setActiveSeatingPreviewMemberKey('');
      setIsModeOpen(false);
      setIsClockFullscreen(false);
      if (isAnnouncementFullscreen && activeAnnouncement?.id) {
        markAnnouncementDismissed(activeAnnouncement.id);
        setIsAnnouncementFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeAnnouncement, isAnnouncementFullscreen, markAnnouncementDismissed]);

  useEffect(() => {
    return () => {
      if (programAnimTimerRef.current) {
        clearTimeout(programAnimTimerRef.current);
        programAnimTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!timetable) {
      programDisplayRef.current = { current: null, next: null };
      setProgramDisplay(programDisplayRef.current);
      setIsProgramAnimating(false);
      return;
    }
    const view = normalizeProgramView(buildProgramView(timetable, nowTimetableRowId, adjustmentsMap));
    const prev = programDisplayRef.current;
    const prevCurrentId = prev.current?.id || null;
    const nextCurrentId = view.current?.id || null;

    if (!prev.current && !prev.next) {
      programDisplayRef.current = view;
      setProgramDisplay(view);
      return;
    }

    if (prevCurrentId === nextCurrentId) {
      if (
        !isSameProgramDisplayItem(prev.current, view.current) ||
        !isSameProgramDisplayItem(prev.next, view.next)
      ) {
        const merged = { current: view.current || prev.current, next: view.next || null };
        programDisplayRef.current = merged;
        setProgramDisplay(merged);
      }
      return;
    }

    if (!prev.current || !view.current) {
      programDisplayRef.current = view;
      setProgramDisplay(view);
      setIsProgramAnimating(false);
      return;
    }

    if (programAnimTimerRef.current) {
      clearTimeout(programAnimTimerRef.current);
      programAnimTimerRef.current = null;
    }

    const animState = {
      current: prev.current,
      next: view.current || prev.next || null,
    };
    programDisplayRef.current = animState;
    setProgramDisplay(animState);
    setIsProgramAnimating(true);
    programAnimTimerRef.current = setTimeout(() => {
      programDisplayRef.current = view;
      setProgramDisplay(view);
      setIsProgramAnimating(false);
    }, PROGRAM_ANIM_DURATION);
  }, [timetable, nowTimetableRowId, adjustmentsMap]);

  useEffect(() => {
    if (!timetable) return undefined;
    const el = tableWrapRef.current;
    if (!el) return undefined;
    const update = () => {
      setTableWrapWidth(el.clientWidth);
      const maxScrollLeft = Math.max(el.scrollWidth - el.clientWidth, 0);
      setTableHasHorizontalOverflow(maxScrollLeft > 1);
      setTableScrolledHorizontally(el.scrollLeft > 1);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [timetable]);

  const handleTableScroll = useCallback((event) => {
    const target = event?.currentTarget;
    if (!target) return;
    const maxScrollLeft = Math.max(target.scrollWidth - target.clientWidth, 0);
    setTableHasHorizontalOverflow(maxScrollLeft > 1);
    setTableScrolledHorizontally(target.scrollLeft > 1);
  }, []);

  const clearHighlightTimer = useCallback(() => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
  }, []);

  const scheduleHighlightTick = useCallback(() => {
    clearHighlightTimer();
    if (isConfirmationPreview) {
      setNowTimetableRowId(null);
      return;
    }
    if (!timetable) {
      setNowTimetableRowId(null);
      return;
    }
    setNowTimetableRowId(findNowRowId(timetable, adjustmentsMap));
    const nextStart = findNextStartMs(timetable, adjustmentsMap);
    if (!nextStart) return;
    const now = Date.now();
    const diff = nextStart - now;
    let delay = 10000;
    if (diff <= 0) delay = 500;
    else if (diff < 1000) delay = Math.max(120, diff);
    else if (diff < 5000) delay = 500;
    else if (diff < 15000) delay = 1000;
    else if (diff < 60000) delay = 3000;
    highlightTimerRef.current = setTimeout(scheduleHighlightTick, delay);
  }, [timetable, adjustmentsMap, clearHighlightTimer, isConfirmationPreview]);

  useEffect(() => {
    scheduleHighlightTick();
    return clearHighlightTimer;
  }, [scheduleHighlightTick, clearHighlightTimer]);

  useEffect(() => {
    if (isConfirmationPreview) return undefined;
    if (!eventId) return undefined;
    const socket = getSocket();
    if (!socket.connected) {
      try {
        socket.connect();
      } catch (err) {
        // ignore connect errors
      }
    }
    try {
      socket.emit(SOCKET_EVENTS.JOIN_EVENT, eventId);
    } catch (err) {
      // ignore join errors
    }
    const eventName = SOCKET_EVENTS.TIMETABLE_RUNTIME_UPDATE || 'timetable_runtime_update';
    const handleRuntimeUpdate = (payload = {}) => {
      if (String(payload.eventId || '') !== String(eventId)) return;
      if (payload.type === 'announcement' && payload.announcement?.id) {
        setActiveAnnouncement(payload.announcement);
        if (!isAnnouncementDismissed(payload.announcement.id)) {
          setIsAnnouncementFullscreen(true);
        }
      }
      fetchRuntime();
    };
    socket.on(eventName, handleRuntimeUpdate);
    return () => {
      socket.off(eventName, handleRuntimeUpdate);
      try {
        socket.emit('leave_event', eventId);
      } catch (err) {
        // ignore leave errors
      }
    };
  }, [eventId, fetchRuntime, isAnnouncementDismissed, isConfirmationPreview]);

  const toggleRowSelection = useCallback((rowId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  }, []);

  const openAdjustmentModal = useCallback(
    (row) => {
      if (!canEditRuntime || !row) return;
      const existing = adjustmentsMap.get(String(row.id)) || 0;
      const mode = existing < 0 ? 'pull' : 'push';
      const minutes = existing ? Math.abs(existing) : 5;
      setAdjustmentModal({
        open: true,
        row,
        mode,
        minutes: String(minutes),
        error: '',
        saving: false,
      });
    },
    [adjustmentsMap, canEditRuntime],
  );

  const openMemoModal = useCallback(
    (row) => {
      if (!canEditRuntime || !row) return;
      const existing = memoMap.get(String(row.id));
      setMemoModal({
        open: true,
        row,
        content: existing?.content || '',
        visibility: existing?.visibility || 'all',
        sendToChat: false,
        error: '',
        saving: false,
      });
    },
    [memoMap, canEditRuntime],
  );

  const closeAdjustmentModal = useCallback(() => {
    setAdjustmentModal((prev) => ({ ...prev, open: false, row: null, error: '', saving: false }));
  }, []);

  const closeMemoModal = useCallback(() => {
    setMemoModal((prev) => ({ ...prev, open: false, row: null, error: '', saving: false }));
  }, []);

  const openAnnouncementModal = useCallback(() => {
    if (!canEditRuntime) return;
    setAnnouncementModal({
      open: true,
      content: '',
      duration: '5',
      visibility: 'all',
      sendToChat: true,
      error: '',
      saving: false,
    });
  }, [canEditRuntime]);

  const closeAnnouncementModal = useCallback(() => {
    setAnnouncementModal((prev) => ({ ...prev, open: false, error: '', saving: false }));
  }, []);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const startLongPress = useCallback(
    (event, row, columnId) => {
      if (!canEditRuntime) return;
      if (event?.pointerType && event.pointerType !== 'touch') return;
      clearLongPress();
      longPressTimerRef.current = window.setTimeout(() => {
        if (columnId === 'startTime') openAdjustmentModal(row);
        else openMemoModal(row);
      }, 550);
    },
    [canEditRuntime, clearLongPress, openAdjustmentModal, openMemoModal],
  );

  const handleCellContextMenu = useCallback(
    (event, row, columnId) => {
      if (!canEditRuntime) return;
      event.preventDefault();
      event.stopPropagation();
      if (columnId === 'startTime') openAdjustmentModal(row);
      else openMemoModal(row);
    },
    [canEditRuntime, openAdjustmentModal, openMemoModal],
  );

  const setRowRef = useCallback(
    (rowId) => (node) => {
      const map = rowRefs.current;
      if (node) {
        map.set(rowId, node);
      } else {
        map.delete(rowId);
      }
    },
    [],
  );

  const scrollRowIntoView = useCallback((rowId, behavior = 'smooth') => {
    if (!rowId) return;
    const container = tableWrapRef.current;
    if (!container) return;
    const row = rowRefs.current.get(rowId);
    if (!row) return;
    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const header = container.querySelector('thead');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    const topLimit = containerRect.top + headerHeight + 4;
    const bottomLimit = containerRect.bottom - 4;
    if (rowRect.top >= topLimit && rowRect.bottom <= bottomLimit) return;
    const offsetTop = rowRect.top - containerRect.top + container.scrollTop - headerHeight - 8;
    const maxScroll = Math.max(container.scrollHeight - container.clientHeight, 0);
    const nextTop = Math.min(Math.max(offsetTop, 0), maxScroll);
    container.scrollTo({ top: nextTop, behavior });
  }, []);

  const submitAdjustment = useCallback(
    async (minutesValue) => {
      if (!eventId || !timetableId || !adjustmentModal.row) return;
      setAdjustmentModal((prev) => ({ ...prev, saving: true, error: '' }));
      try {
        await upsertEventTimetableAdjustment(eventId, timetableId, {
          row_id: adjustmentModal.row.id,
          minutes: minutesValue,
        });
        closeAdjustmentModal();
        fetchRuntime();
      } catch (err) {
        setAdjustmentModal((prev) => ({
          ...prev,
          saving: false,
          error: err.message || '押し巻き設定の保存に失敗しました',
        }));
      }
    },
    [adjustmentModal.row, closeAdjustmentModal, eventId, fetchRuntime, timetableId],
  );

  const handleSaveAdjustment = useCallback(() => {
    const minutesNum = Number(adjustmentModal.minutes);
    if (!Number.isFinite(minutesNum) || minutesNum < 0) {
      setAdjustmentModal((prev) => ({ ...prev, error: '分数は0以上で入力してください' }));
      return;
    }
    const delta = adjustmentModal.mode === 'pull' ? -minutesNum : minutesNum;
    submitAdjustment(delta);
  }, [adjustmentModal.mode, adjustmentModal.minutes, submitAdjustment]);

  const handleClearAdjustment = useCallback(() => {
    submitAdjustment(0);
  }, [submitAdjustment]);

  const submitMemo = useCallback(
    async (nextContent) => {
      if (!eventId || !timetableId || !memoModal.row) return;
      setMemoModal((prev) => ({ ...prev, saving: true, error: '' }));
      const row = memoModal.row;
      const rowTime =
        row.adjustedOffsetMinutes && row.adjustedStartTime ? row.adjustedStartTime : row.startTime;
      const rowItem = getRowItemText(row);
      const rowLabel = [rowTime, rowItem].filter(Boolean).join(' ').trim();
      try {
        await upsertEventTimetableMemo(eventId, timetableId, {
          row_id: row.id,
          content: nextContent,
          visibility: memoModal.visibility,
          send_to_chat: memoModal.sendToChat,
          row_time: rowTime || '',
          row_item: rowItem || '',
          row_label: rowLabel || '',
        });
        closeMemoModal();
        fetchRuntime();
      } catch (err) {
        setMemoModal((prev) => ({
          ...prev,
          saving: false,
          error: err.message || 'メモの保存に失敗しました',
        }));
      }
    },
    [memoModal, closeMemoModal, eventId, fetchRuntime, timetableId],
  );

  const handleSaveMemo = useCallback(() => {
    submitMemo(memoModal.content.trim());
  }, [memoModal.content, submitMemo]);

  const handleDeleteMemo = useCallback(() => {
    submitMemo('');
  }, [submitMemo]);

  const handleSaveAnnouncement = useCallback(async () => {
    if (!eventId || !timetableId) return;
    const content = announcementModal.content.trim();
    if (!content) {
      setAnnouncementModal((prev) => ({ ...prev, error: '内容を入力してください' }));
      return;
    }
    const durationNum = Number(announcementModal.duration);
    if (!Number.isFinite(durationNum) || durationNum <= 0) {
      setAnnouncementModal((prev) => ({ ...prev, error: '表示時間を正しく入力してください' }));
      return;
    }
    setAnnouncementModal((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      const json = await createEventTimetableAnnouncement(eventId, {
        content,
        duration_minutes: durationNum,
        visibility: announcementModal.visibility,
        send_to_chat: announcementModal.sendToChat,
        timetable_id: Number(timetableId),
      });
      closeAnnouncementModal();
      fetchRuntime();
      if (json?.announcement?.id) {
        setActiveAnnouncement(json.announcement);
        if (!isAnnouncementDismissed(json.announcement.id)) {
          setIsAnnouncementFullscreen(true);
        }
      }
    } catch (err) {
      setAnnouncementModal((prev) => ({
        ...prev,
        saving: false,
        error: err.message || 'お知らせの追加に失敗しました',
      }));
    }
  }, [announcementModal, closeAnnouncementModal, eventId, fetchRuntime, isAnnouncementDismissed, timetableId]);

  const closeAnnouncementFullscreen = useCallback(() => {
    if (activeAnnouncement?.id) markAnnouncementDismissed(activeAnnouncement.id);
    setIsAnnouncementFullscreen(false);
  }, [activeAnnouncement, markAnnouncementDismissed]);

  useEffect(() => {
    if (!nowTimetableRowId) {
      prevNowRowIdRef.current = nowTimetableRowId;
      return;
    }
    if (prevNowRowIdRef.current === nowTimetableRowId) return;
    prevNowRowIdRef.current = nowTimetableRowId;
    const rafId = window.requestAnimationFrame(() => {
      scrollRowIntoView(nowTimetableRowId, 'smooth');
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [nowTimetableRowId, scrollRowIntoView]);

  const openClockFullscreen = useCallback(() => {
    setIsClockFullscreen(true);
  }, []);

  const closeClockFullscreen = useCallback(() => {
    setIsClockFullscreen(false);
  }, []);

  const openModeModal = useCallback(() => {
    setIsModeOpen(true);
  }, []);

  const closeModeModal = useCallback(() => {
    setIsModeOpen(false);
  }, []);

  const selectColorMode = useCallback((mode) => {
    setColorMode(mode);
    setIsModeOpen(false);
  }, []);

  const toggleHeaderMenu = useCallback(() => {
    setIsHeaderMenuOpen((current) => !current);
  }, []);

  const closeHeaderMenu = useCallback(() => {
    setIsHeaderMenuOpen(false);
  }, []);

  const fetchPublicTimetableLinks = useCallback(async ({ silent = false } = {}) => {
    if (mode !== 'admin' || !eventId || !timetableId) return;
    if (!silent) {
      setPublicLinksLoading(true);
      setPublicLinksError('');
    }
    try {
      const json = await listStagePreviewPublicLinks(eventId);
      const targetId = Number(timetableId);
      const links = Array.isArray(json.links)
        ? json.links.filter((link) => (
          link.link_type === 'timetable' && Number(link.target_timetable_id) === targetId
        ))
        : [];
      setPublicLinks(links);
      setPublicLinksError('');
    } catch (err) {
      const message = err?.message || '限定リンク一覧の取得に失敗しました';
      setPublicLinksError(message);
      if (!silent) toastError(message);
    } finally {
      if (!silent) setPublicLinksLoading(false);
    }
  }, [eventId, mode, timetableId, toastError]);

  const openPublicTimetableLinkModal = useCallback(() => {
    if (mode !== 'admin') return;
    closeHeaderMenu();
    setPublicLinkModalOpen(true);
    fetchPublicTimetableLinks();
  }, [closeHeaderMenu, fetchPublicTimetableLinks, mode]);

  const closePublicTimetableLinkModal = useCallback(() => {
    setPublicLinkModalOpen(false);
    setPublicLinksError('');
  }, []);

  const copyKnownPublicLinkUrl = useCallback(async (url) => {
    const value = String(url || '').trim();
    if (!value) {
      toastError('このリンクのURLは安全のため保存されていません。必要な場合は新しく作成してください。');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toastSuccess('限定リンクをコピーしました');
    } catch (err) {
      toastError('コピーできませんでした。URL欄を選択してコピーしてください。');
    }
  }, [toastError, toastSuccess]);

  const openSeatingPreview = useCallback((row, layouts) => {
    const list = Array.isArray(layouts) ? layouts : [];
    if (!row || !list.length) return;
    setSeatingPreview({
      open: true,
      row,
      layoutId: list[0]?.id ?? null,
    });
    setActiveSeatingPreviewMemberKey('');
  }, []);

  const closeSeatingPreview = useCallback(() => {
    setSeatingPreview({ open: false, row: null, layoutId: null });
    setActiveSeatingPreviewMemberKey('');
  }, []);

  const openFullSeatingPreview = useCallback((layoutId) => {
    if (!eventId || !layoutId || typeof window === 'undefined') return;
    const basePath = mode === 'artist'
      ? `/artist/workspaces/event-operations/events/${encodeURIComponent(eventId)}/stage-seating/preview`
      : `/admin/workspaces/event-operations/events/${encodeURIComponent(eventId)}/stage-seating/preview`;
    const url = `${basePath}?layout_id=${encodeURIComponent(layoutId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [eventId, mode]);

  const createPublicTimetableLink = useCallback(async () => {
    if (mode !== 'admin' || publicLinkCreating || !eventId || !timetableId) return;
    setPublicLinkCreating(true);
    try {
      const json = await createStagePreviewPublicLink(
        eventId,
        {
          link_type: 'timetable',
          timetable_id: Number(timetableId),
          expires_days: 30,
        },
        `timetable-public-${Date.now()}-${Math.random().toString(36).slice(2)}`
      );
      if (!json?.url) throw new Error('限定リンクの作成に失敗しました');
      try {
        await navigator.clipboard.writeText(json.url);
        toastSuccess('限定リンクをコピーしました');
      } catch (err) {
        toastSuccess('限定リンクを作成しました。URL欄からコピーできます。');
      }
      const createdLinkId = Number(json?.link?.id ?? json?.id);
      if (Number.isSafeInteger(createdLinkId) && createdLinkId > 0) {
        setPublicLinkUrlsById((current) => ({ ...current, [createdLinkId]: json.url }));
      }
      await fetchPublicTimetableLinks({ silent: true });
    } catch (err) {
      toastError(err?.message || '限定リンクの作成に失敗しました');
    } finally {
      setPublicLinkCreating(false);
    }
  }, [eventId, fetchPublicTimetableLinks, mode, publicLinkCreating, timetableId, toastError, toastSuccess]);

  const revokePublicTimetableLink = useCallback(async (linkId) => {
    if (mode !== 'admin' || publicLinkRevokingId) return;
    const id = Number(linkId);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    setPublicLinkRevokingId(id);
    try {
      const json = await revokeStagePreviewPublicLink(
        eventId,
        id,
        `timetable-public-revoke-${Date.now()}-${Math.random().toString(36).slice(2)}`
      );
      setPublicLinks((current) => current.map((link) => (
        Number(link.id) === id
          ? { ...link, status: 'revoked', can_revoke: false, revoked_at: json.link?.revoked_at || new Date().toISOString() }
          : link
      )));
      toastSuccess('限定リンクを失効しました');
    } catch (err) {
      toastError(err?.message || '限定リンクの失効に失敗しました');
    } finally {
      setPublicLinkRevokingId(null);
    }
  }, [eventId, mode, publicLinkRevokingId, toastError, toastSuccess]);

  const openModeModalFromMenu = useCallback(() => {
    closeHeaderMenu();
    openModeModal();
  }, [closeHeaderMenu, openModeModal]);

  const openAnnouncementModalFromMenu = useCallback(() => {
    closeHeaderMenu();
    openAnnouncementModal();
  }, [closeHeaderMenu, openAnnouncementModal]);

  useEffect(() => {
    if (!isHeaderMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (headerMenuRef.current?.contains(event.target)) return;
      setIsHeaderMenuOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isHeaderMenuOpen]);

  useEffect(() => {
    const next = Array.isArray(runtimeData?.announcements)
      ? runtimeData.announcements[0] || null
      : null;
    setActiveAnnouncement(next);
  }, [runtimeData?.announcements]);

  useEffect(() => {
    if (!activeAnnouncement?.id) {
      setIsAnnouncementFullscreen(false);
      return;
    }
    if (isAnnouncementDismissed(activeAnnouncement.id)) return;
    setIsAnnouncementFullscreen(true);
  }, [activeAnnouncement, isAnnouncementDismissed]);

  useEffect(() => {
    if (!activeAnnouncement?.end_at) return undefined;
    const endMs = new Date(activeAnnouncement.end_at).getTime();
    if (!Number.isFinite(endMs)) return undefined;
    const now = Date.now();
    if (endMs <= now) {
      fetchRuntime();
      return undefined;
    }
    const timer = window.setTimeout(() => {
      fetchRuntime();
    }, Math.max(200, endMs - now + 200));
    return () => window.clearTimeout(timer);
  }, [activeAnnouncement, fetchRuntime]);

  const renderTimetable = useCallback(
    (table) => {
      if (!table) return null;
      const data = table.data || {};
      const baseColumns = buildPreviewColumns(data);
      const columns = baseColumns.filter((c) => c.id !== 'duration');
      const hasDurationColumn = baseColumns.some((c) => c.id === 'duration');
      const rows = applyRowAdjustments(buildPreviewRows(data), adjustmentsMap);
      const columnWidths = pickColumnWidths(data, columns);
      const rowHeights = pickRowHeights(data, rows);
      const titleStyle = pickTitleStyle(data);
      const headerGroups = buildHeaderGroups(columns);
      const estimatedStickyColWidth = estimateTimeColumnWidth({
        rows,
        label: columns.find((c) => c.id === 'startTime')?.label || '時刻',
        hasDurationColumn,
        titleStyle,
        containerWidth: tableWrapWidth,
      });
      const timeCellPaddingX =
        resolveTimetableViewportWidth(tableWrapWidth) <= SP_BREAKPOINT
          ? TIME_COLUMN_SIDE_PADDING_SP
          : TIME_COLUMN_SIDE_PADDING_PC;
      const scaledWidths = scaleColumnWidths({ ...columnWidths, startTime: estimatedStickyColWidth }, columns, tableWrapWidth);
      const stickyColWidth =
        typeof scaledWidths.startTime === 'number' && scaledWidths.startTime > 0 ? scaledWidths.startTime : 120;

      const colGroup =
        columns.length > 0 ? (
          <colgroup>
            {columns.map((c) => {
              const w = scaledWidths[c.id];
              const width = typeof w === 'number' && w > 0 ? `${w}px` : null;
              return <col key={c.id} style={width ? { width, minWidth: width } : undefined} />;
            })}
          </colgroup>
        ) : null;

      const header = (
        <tr className="timetable-title-row">
          {headerGroups.map((g, idx) => {
            const isSticky = g.startId === 'startTime';
            return (
              <th
                key={`header-${idx}`}
                colSpan={g.span}
                className={isSticky ? 'is-sticky-col' : undefined}
                style={{
                  background: titleStyle.bgColor || DEFAULT_TITLE_STYLE.bgColor,
                  color: titleStyle.textColor || DEFAULT_TITLE_STYLE.textColor,
                  fontWeight: titleStyle.bold ? 700 : 600,
                  fontSize: `${titleStyle.fontSize || DEFAULT_TITLE_STYLE.fontSize}px`,
                  textAlign: g.align || 'left',
                  height: titleStyle.height ? `${titleStyle.height}px` : undefined,
                  padding: isSticky ? `6px ${timeCellPaddingX}px` : undefined,
                }}
              >
                {g.label || ''}
              </th>
            );
          })}
        </tr>
      );

      const body = [];
      rows.forEach((r) => {
        const rowHeight = rowHeights[r.id];
        const isNow = nowTimetableRowId === r.id;
        const isSelected = selectedRowIds.has(r.id);
        const highlightRow = isNow || isSelected;
        const rowClassName = [isNow ? 'is-now' : '', isSelected ? 'is-selected' : '']
          .filter(Boolean)
          .join(' ');
        const memo = memoMap.get(String(r.id));
        const linkedLayouts = linkedLayoutsByRowId.get(String(r.id)) || [];
        const hasAdjustedTime =
          r.adjustedOffsetMinutes && r.adjustedStartTime && String(r.startTime || '').trim();
        const displayStartTime = hasAdjustedTime ? r.adjustedStartTime : r.startTime || '';
        const rawDuration = hasDurationColumn ? getRowDurationValue(r) : '';
        const durationText =
          rawDuration !== null && rawDuration !== undefined ? String(rawDuration).trim() : '';

        body.push(
          <tr
            key={r.id}
            ref={setRowRef(r.id)}
            className={rowClassName}
            onClick={() => toggleRowSelection(r.id)}
            style={rowHeight ? { height: `${rowHeight}px` } : undefined}
          >
            {columns.map((c) => {
              const cell = (r.cells && r.cells[c.id]) || {};
              if (cell.mergeParentId) return null;
              const spanProps = {
                rowSpan: cell.rowSpan || 1,
                colSpan: cell.colSpan || 1,
              };
              let value = '';
              if (c.id === 'startTime') {
                let timeNode = '';
                if (hasAdjustedTime) {
                  const mode = r.adjustedOffsetMinutes > 0 ? 'is-push' : 'is-pull';
                  timeNode = (
                    <div className={`tt-time-adjusted ${mode}`}>
                      <span className="tt-time-original">{r.startTime || ''}</span>
                      <span className="tt-time-arrow">→</span>
                      <span className="tt-time-new">{displayStartTime}</span>
                    </div>
                  );
                } else {
                  timeNode = displayStartTime || '';
                }
                if (timeNode || durationText) {
                  value = (
                    <div className="tt-time-stack">
                      {timeNode ? <div className="tt-time-main">{timeNode}</div> : null}
                      {durationText ? (
                        <div className="tt-time-duration">({durationText})</div>
                      ) : null}
                    </div>
                  );
                } else {
                  value = '';
                }
              } else if (c.id === 'duration') value = r.duration || '';
              else if (c.id === 'item') {
                const itemText = r.item || getRowItemText(r);
                value = linkedLayouts.length ? (
                  <div className="tt-item-with-seating">
                    <span className="tt-item-text">{itemText}</span>
                    <button
                      type="button"
                      className="tt-seating-preview-trigger"
                      aria-label="連動シーティングを表示"
                      title="連動シーティングを表示"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openSeatingPreview(r, linkedLayouts);
                      }}
                    >
                      <i className="fa-solid fa-border-all" aria-hidden="true" />
                      {linkedLayouts.length > 1 ? <em>{linkedLayouts.length}</em> : null}
                    </button>
                  </div>
                ) : itemText;
              }
              else value = cell.value || '';
              const bg = cell.color || r.rowColor || '';
              const txt = cell.textColor || r.rowTextColor || '';
              const align = c.align || getDefaultColumnAlign(c.id);
              const isStartTime = c.id === 'startTime';
              return (
                <td
                  key={`${r.id}-${c.id}`}
                  {...spanProps}
                  className={isStartTime ? 'is-sticky-col' : undefined}
                  style={{
                    textAlign: align,
                    background: highlightRow ? undefined : bg || undefined,
                    color: highlightRow ? undefined : txt || undefined,
                    fontWeight: cell.bold ? 700 : undefined,
                    fontSize: cell.fontSize ? `${cell.fontSize}px` : undefined,
                    whiteSpace: 'pre-wrap',
                    verticalAlign: 'middle',
                    padding: isStartTime ? `6px ${timeCellPaddingX}px` : '8px 10px',
                  }}
                  onContextMenu={(event) => handleCellContextMenu(event, r, c.id)}
                  onPointerDown={(event) => startLongPress(event, r, c.id)}
                  onPointerUp={clearLongPress}
                  onPointerLeave={clearLongPress}
                  onPointerCancel={clearLongPress}
                >
                  {value}
                </td>
              );
            })}
          </tr>,
        );

        if (memo?.content) {
          const memoVisibility = memo.visibility || 'all';
          const hasStickyStartColumn = columns[0]?.id === 'startTime';
          const memoContent = (
            <div className="timetable-row-memo-content">
              <span className="timetable-row-memo-label">メモ</span>
              {memoVisibility === 'admin' ? (
                <span className="timetable-row-memo-badge">管理者</span>
              ) : null}
              <span className="timetable-row-memo-text">{memo.content}</span>
            </div>
          );
          body.push(
            <tr key={`${r.id}-memo`} className="timetable-row-memo">
              {hasStickyStartColumn ? (
                <>
                  <td className="timetable-row-memo-cell timetable-row-memo-sticky is-sticky-col" aria-hidden="true" />
                  <td colSpan={Math.max(columns.length - 1, 1)} className="timetable-row-memo-cell">
                    {memoContent}
                  </td>
                </>
              ) : (
                <td colSpan={columns.length} className="timetable-row-memo-cell">
                  {memoContent}
                </td>
              )}
            </tr>,
          );
        }
      });

      return (
        <div className="timetable-standalone-wrap">
          <div
            className={[
              'timetable-standalone-scroll',
              tableHasHorizontalOverflow ? 'has-x-overflow' : '',
              tableScrolledHorizontally ? 'is-x-scrolled' : '',
            ].filter(Boolean).join(' ')}
            style={{ '--tt-sticky-col-width': `${stickyColWidth}px` }}
          >
            <div
              className="timetable-standalone-scroll-inner"
              ref={tableWrapRef}
              onScroll={handleTableScroll}
            >
              <table className="timetable-standalone-table">
                {colGroup}
                <thead>{header}</thead>
                <tbody>{body}</tbody>
              </table>
            </div>
          </div>
        </div>
      );
    },
    [
      nowTimetableRowId,
      selectedRowIds,
      setRowRef,
      tableWrapWidth,
      tableHasHorizontalOverflow,
      tableScrolledHorizontally,
      toggleRowSelection,
      adjustmentsMap,
      memoMap,
      linkedLayoutsByRowId,
      openSeatingPreview,
      handleCellContextMenu,
      handleTableScroll,
      startLongPress,
      clearLongPress,
    ],
  );

  const nowTimeOnly = nowLabel.includes(' ') ? nowLabel.split(' ')[1] : nowLabel;
  const timetableTitleRaw = timetable?.title || timetable?.name || 'タイムテーブル';
  const timetableTitle = String(timetableTitleRaw || '').trim() || 'タイムテーブル';
  const timetableDate = timetable
    ? toYmdSlash(timetable.target_date) || '日付未設定'
    : '日付未設定';
  const fallbackEventLabel = buildEventLabel(timetable);
  const timetablePerformanceLabel = buildTimetablePerformanceLabel(timetable);
  const displayEventLabel = [eventLabel || fallbackEventLabel, timetablePerformanceLabel].filter(Boolean).join(' ');
  const programCurrent = programDisplay.current;
  const programNext = programDisplay.next;
  const adjustmentRow = adjustmentModal.row;
  const adjustmentRowTime =
    adjustmentRow && adjustmentRow.adjustedOffsetMinutes && adjustmentRow.adjustedStartTime
      ? adjustmentRow.adjustedStartTime
      : adjustmentRow?.startTime || '';
  const adjustmentRowItem = adjustmentRow ? getRowItemText(adjustmentRow) : '';
  const memoRow = memoModal.row;
  const memoRowTime =
    memoRow && memoRow.adjustedOffsetMinutes && memoRow.adjustedStartTime
      ? memoRow.adjustedStartTime
      : memoRow?.startTime || '';
  const memoRowItem = memoRow ? getRowItemText(memoRow) : '';
  const seatingPreviewLayouts = seatingPreview.row
    ? (linkedLayoutsByRowId.get(String(seatingPreview.row.id)) || [])
    : [];
  const seatingPreviewLayout =
    seatingPreviewLayouts.find((layout) => String(layout.id) === String(seatingPreview.layoutId))
    || seatingPreviewLayouts[0]
    || null;
  const seatingPreviewData = seatingPreviewLayout?.normalized_layout_data
    || normalizeSeatingLayoutData(seatingPreviewLayout?.layout_data || {});
  const seatingPreviewCanvas = seatingPreviewData.canvas || {};
  const seatingPreviewAspect = getCanvasAspectRatioCss(seatingPreviewCanvas.aspect_ratio);
  const seatingPreviewBackgroundColor = normalizeCanvasBackgroundColor(seatingPreviewCanvas.background_color);
  const seatingPreviewIconSize = normalizeIconSize(seatingPreviewData.icon_size);
  const seatingPreviewRowTime =
    seatingPreview.row && seatingPreview.row.adjustedOffsetMinutes && seatingPreview.row.adjustedStartTime
      ? seatingPreview.row.adjustedStartTime
      : seatingPreview.row?.startTime || '';
  const seatingPreviewRowItem = seatingPreview.row ? getRowItemText(seatingPreview.row) : '';
  const seatingPreviewCustomIconById = new Map(
    (seatingPreviewData.custom_icons || []).map((icon) => [String(icon.id), icon]),
  );
  const seatingPreviewBackground = seatingPreviewData.background || null;
  const seatingPreviewParts = [
    ...(seatingPreviewData.shapes || []).map((item, index) => ({
      key: `shape-${item.id || index}`,
      kind: 'shape',
      item,
      z: Number(item.z_index || 0),
    })),
    ...(seatingPreviewData.text_items || []).map((item, index) => ({
      key: `text-${item.id || index}`,
      kind: 'text',
      item,
      z: Number(item.z_index || 0) + 1000,
    })),
  ].sort((a, b) => a.z - b.z);

  const renderSeatingPreviewModal = () => {
    if (!seatingPreview.open || !seatingPreviewLayout) return null;
    return (
      <DsModal
        open
        onClose={closeSeatingPreview}
        ariaLabel="シーティング簡易プレビュー"
        showClose={false}
        size="lg"
        className="tt-seating-preview-card"
      >
          <div className="tt-seating-preview-head">
            <div>
              <h2>{seatingPreviewLayout.title || 'シーティング'}</h2>
              <p>{[formatStartTime(seatingPreviewRowTime), seatingPreviewRowItem].filter(Boolean).join('  ')}</p>
            </div>
            <div className="tt-seating-preview-actions">
              <button
                type="button"
                className="tt-seating-preview-open"
                aria-label="正式プレビューを別タブで開く"
                title="正式プレビューを別タブで開く"
                onClick={() => openFullSeatingPreview(seatingPreviewLayout.id)}
              >
                <i className="fa-solid fa-up-right-from-square" aria-hidden="true" />
              </button>
              <button type="button" className="tt-seating-preview-close" onClick={closeSeatingPreview} aria-label="閉じる">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
          </div>
          {seatingPreviewLayouts.length > 1 ? (
            <div className="tt-seating-preview-tabs" role="tablist" aria-label="紐づくシーティング">
              {seatingPreviewLayouts.map((layout) => (
                <button
                  key={layout.id}
                  type="button"
                  className={String(layout.id) === String(seatingPreviewLayout.id) ? 'is-active' : ''}
                  onClick={() => {
                    setSeatingPreview((prev) => ({ ...prev, layoutId: layout.id }));
                    setActiveSeatingPreviewMemberKey('');
                  }}
                >
                  {layout.title || '無題'}
                </button>
              ))}
            </div>
          ) : null}
          <div className="tt-seating-preview-stage-shell">
            <div
              className="tt-seating-preview-stage"
              style={{
                aspectRatio: seatingPreviewAspect,
                backgroundColor: seatingPreviewBackgroundColor,
                '--tt-stage-icon-size': `${seatingPreviewIconSize}px`,
              }}
            >
              {seatingPreviewBackground?.type === 'image' ? (
                <img
                  className={`tt-seating-preview-background fit-${seatingPreviewBackground.fit || 'contain'}`}
                  src={seatingPreviewBackground.data_url}
                  alt=""
                />
              ) : null}
              {seatingPreviewParts.map((part) => {
                if (part.kind === 'text') {
                  const item = part.item;
                  return (
                    <div
                      key={part.key}
                      className="tt-seating-preview-text"
                      style={{
                        left: `${item.x}%`,
                        top: `${item.y}%`,
                        width: `${item.width}%`,
                        height: `${item.height}%`,
                        color: item.color,
                        fontSize: `${item.size}px`,
                        fontWeight: item.weight,
                        opacity: item.opacity,
                        textAlign: item.align,
                        transform: `translate(-50%, -50%) rotate(${item.rotation}deg)`,
                      }}
                    >
                      {item.text}
                    </div>
                  );
                }
                const shape = part.item;
                return (
                  <div
                    key={part.key}
                    className={`tt-seating-preview-shape shape-${shape.type}`}
                    style={{
                      left: `${shape.x}%`,
                      top: `${shape.y}%`,
                      width: `${shape.width}%`,
                      height: `${shape.height}%`,
                      transform: `translate(-50%, -50%) rotate(${shape.rotation}deg)`,
                      '--tt-shape-fill': shape.fill,
                      '--tt-shape-stroke': shape.stroke,
                      opacity: shape.opacity,
                    }}
                  >
                    {shape.label ? <span style={{ transform: `rotate(${-shape.rotation}deg)` }}>{shape.label}</span> : null}
                  </div>
                );
              })}
              {(seatingPreviewData.custom_placements || []).map((placement) => {
                const icon = seatingPreviewCustomIconById.get(String(placement.custom_id));
                if (!icon) return null;
                const memberKey = `custom:${placement.custom_id}`;
                const isActive = activeSeatingPreviewMemberKey === memberKey;
                return (
                  <div
                    key={placement.custom_id}
                    className={`tt-seating-preview-member tt-seating-preview-custom${isActive ? ' is-active' : ''}`}
                    style={{
                      left: `${placement.x}%`,
                      top: `${placement.y}%`,
                      '--tt-member-color': icon.color || 'var(--purple-600)',
                      zIndex: isActive ? 5000 : 20,
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveSeatingPreviewMemberKey(memberKey);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setActiveSeatingPreviewMemberKey(memberKey);
                    }}
                  >
                    <div className="tt-seating-preview-avatar">{icon.icon_text || icon.label?.charAt(0) || '?'}</div>
                    <span className="tt-seating-preview-label">
                      <span className="tt-seating-preview-label-main">{icon.label || '自由アイコン'}</span>
                      {icon.role ? <span className="tt-seating-preview-label-part">{icon.role}</span> : null}
                    </span>
                  </div>
                );
              })}
              {(seatingPreviewData.placements || []).map((placement) => {
                const artist = participantByArtistId.get(String(placement.artist_id));
                const name = artist?.name || artist?.artist_name || '名称未設定';
                const partLabel = artist?.part_label || artist?.partLabel || artist?.part || 'パート未設定';
                const memberKey = `artist:${placement.artist_id}`;
                const isActive = activeSeatingPreviewMemberKey === memberKey;
                const roleTags = [
                  artist?.is_concertmaster || artist?.isConcertmaster ? 'コンサートマスター' : '',
                  artist?.is_chief || artist?.isChief ? '首席' : '',
                ].filter(Boolean);
                const imageUrl = buildProfileImageUrl(artist?.profile_picture || artist?.artist_profile_picture || artist?.pic);
                return (
                  <div
                    key={placement.artist_id}
                    className={`tt-seating-preview-member${isActive ? ' is-active' : ''}`}
                    style={{
                      left: `${placement.x}%`,
                      top: `${placement.y}%`,
                      zIndex: isActive ? 5000 : 20,
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isActive}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveSeatingPreviewMemberKey(memberKey);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      setActiveSeatingPreviewMemberKey(memberKey);
                    }}
                  >
                    <div className="tt-seating-preview-avatar">
                      <AvatarImage url={imageUrl} initial={name.charAt(0) || '?'} />
                    </div>
                    <span className="tt-seating-preview-label">
                      <span className="tt-seating-preview-label-main">{name}</span>
                      <span className="tt-seating-preview-label-part">{partLabel}</span>
                      {roleTags.length ? (
                        <span className="tt-seating-preview-label-role">{roleTags.join(' / ')}</span>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
      </DsModal>
    );
  };

  return (
    <div className={`timetable-standalone-page ${colorMode === 'dark' ? 'is-dark' : 'is-light'}`}>
      <div className="timetable-standalone-scale">
        <div className="timetable-standalone-header">
          <div className="timetable-standalone-header-top">
            <div className="timetable-standalone-meta">
              {isConfirmationPreview ? <div className="timetable-view-kind">保存内容の完成確認</div> : null}
              {displayEventLabel ? (
                <div className="timetable-standalone-event">{displayEventLabel}</div>
              ) : null}
              <div className="timetable-standalone-meta-row">
                <div className="timetable-standalone-date">{timetableDate}</div>
                <div className="timetable-standalone-title">{timetableTitle}</div>
              </div>
            </div>
            <div className="timetable-standalone-header-actions" ref={headerMenuRef}>
              {isConfirmationPreview ? (
                <a
                  className="timetable-runtime-open-link"
                  href={`/admin/workspaces/event-operations/events/${encodeURIComponent(eventId)}/timetables/${encodeURIComponent(timetableId)}`}
                >
                  当日運用を開く
                </a>
              ) : null}
              <button
                type="button"
                className={`timetable-standalone-menu-button ${isHeaderMenuOpen ? 'is-open' : ''}`}
                onClick={toggleHeaderMenu}
                aria-label="タイムテーブルメニュー"
                aria-expanded={isHeaderMenuOpen}
              >
                <i className="fa-solid fa-ellipsis" />
              </button>
              {isHeaderMenuOpen ? (
                <div className="timetable-standalone-header-menu" role="menu">
                  {mode === 'admin' && !isConfirmationPreview ? (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={openPublicTimetableLinkModal}
                    >
                      <i className="fa-solid fa-link" />
                      <span>外部リンク</span>
                    </button>
                  ) : null}
                  {canEditRuntime ? (
                    <button type="button" role="menuitem" onClick={openAnnouncementModalFromMenu}>
                      <i className="fa-solid fa-bullhorn" />
                      <span>お知らせ</span>
                    </button>
                  ) : null}
                  <button type="button" role="menuitem" onClick={openModeModalFromMenu}>
                    <i className={`fa-solid ${colorMode === 'dark' ? 'fa-moon' : 'fa-sun'}`} />
                    <span>表示モード</span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          {!isConfirmationPreview ? <div className="timetable-standalone-clock-row">
            <button
              type="button"
              className="timetable-standalone-clock"
              onClick={openClockFullscreen}
              aria-live="polite"
              aria-label="現在時刻を全画面表示"
            >
              <span className="clock-label">現在時刻</span>
              <span className="clock-time">{nowLabel || '—'}</span>
            </button>
          </div> : null}
        </div>
        <div className="timetable-standalone-body">
          {!isConfirmationPreview && activeAnnouncement ? (
            <div className="timetable-standalone-announcement-banner" role="status" aria-live="polite">
              <div className="timetable-standalone-announcement-title">お知らせ</div>
              {activeAnnouncement.visibility === 'admin' ? (
                <span className="timetable-standalone-announcement-badge">管理者</span>
              ) : null}
              <div className="timetable-standalone-announcement-text">{activeAnnouncement.content}</div>
            </div>
          ) : null}
          {runtimeError ? (
            <div className="timetable-standalone-runtime-error">{runtimeError}</div>
          ) : null}
          {loading ? (
            <div className="timetable-standalone-loading">
              <TimetableViewerSkeleton rows={5} columns={4} />
            </div>
          ) : error ? (
            <div className="timetable-standalone-error">{error}</div>
          ) : timetable ? (
            renderTimetable(timetable)
          ) : (
            <div className="timetable-standalone-empty">タイムテーブルがありません。</div>
          )}
        </div>
      </div>
      {publicLinkModalOpen ? (
        <TimetableStandaloneModal
          open={publicLinkModalOpen}
          onClose={closePublicTimetableLinkModal}
          title="外部共有リンク"
          colorMode={colorMode}
          className="timetable-public-link-modal"
        >
            <div className="timetable-public-link-target">
              <strong>{timetableTitle}</strong>
              <span>作成済みのURLはいつでも再コピーできます。</span>
            </div>
            <div className="timetable-public-link-actions">
              <button
                type="button"
                className="timetable-runtime-action timetable-runtime-action--primary"
                onClick={createPublicTimetableLink}
                disabled={publicLinkCreating || !timetableId}
              >
                {publicLinkCreating ? '作成中...' : '作成してコピー'}
              </button>
            </div>
            <div className="timetable-public-link-note">
              失効したリンクは外部スタッフや未ログインユーザーが開けなくなります。
            </div>
            <div className="timetable-public-link-list-head">
              <span>発行済みリンク</span>
              <strong>{publicLinks.length}</strong>
            </div>
            {publicLinksError ? (
              <div className="timetable-public-link-empty">{publicLinksError}</div>
            ) : publicLinksLoading ? (
              <ActionRowsSkeleton
                rows={3}
                containerClassName="timetable-public-link-list"
                itemClassName="timetable-public-link-row"
                mainClassName="timetable-public-link-main"
                actionsClassName="timetable-public-link-actions"
                actionCount={2}
              />
            ) : publicLinks.length ? (
              <div className="timetable-public-link-list">
                {publicLinks.map((link) => {
                  const publicUrl = publicLinkUrlsById[Number(link.id)] || link.url || '';
                  return (
                    <div key={link.id} className={`timetable-public-link-row is-${link.status || 'unknown'}`}>
                      <div className="timetable-public-link-main">
                        <div className="timetable-public-link-title">
                          <span className={`timetable-public-link-status is-${link.status || 'unknown'}`}>
                            {getPublicLinkStatusLabel(link.status)}
                          </span>
                          <strong>{link.target_label || 'タイムテーブル'}</strong>
                        </div>
                        <div className="timetable-public-link-meta">
                          <span>発行 {formatPublicLinkDate(link.created_at)}</span>
                          <span>期限 {formatPublicLinkDate(link.expires_at)}</span>
                          {link.revoked_at ? <span>失効 {formatPublicLinkDate(link.revoked_at)}</span> : null}
                        </div>
                        {publicUrl ? (
                          <input
                            className="timetable-public-link-url"
                            type="text"
                            value={publicUrl}
                            readOnly
                            onClick={(event) => event.currentTarget.select()}
                            aria-label="限定リンクURL"
                          />
                        ) : (
                          <div className="timetable-public-link-url-note">
                            古い形式のリンクです。再コピーすると新しい形式のURLを作成してコピーします。
                          </div>
                        )}
                      </div>
                      <div className="timetable-public-link-row-actions">
                        <button
                          type="button"
                          className="timetable-public-link-copy"
                          onClick={() => (publicUrl ? copyKnownPublicLinkUrl(publicUrl) : createPublicTimetableLink())}
                          disabled={link.status !== 'active' || publicLinkCreating}
                        >
                          再コピー
                        </button>
                        <button
                          type="button"
                          className="timetable-public-link-revoke"
                          onClick={() => revokePublicTimetableLink(link.id)}
                          disabled={!link.can_revoke || publicLinkRevokingId === Number(link.id)}
                        >
                          {publicLinkRevokingId === Number(link.id) ? '失効中' : '失効'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="timetable-public-link-empty">発行済み限定リンクはありません。</div>
            )}
        </TimetableStandaloneModal>
      ) : null}
      {isModeOpen ? (
        <TimetableStandaloneModal open={isModeOpen} onClose={closeModeModal} title="カラーモード" colorMode={colorMode}>
            <div className="timetable-standalone-modal-options">
              <button
                type="button"
                className={`timetable-standalone-mode-option ${colorMode === 'light' ? 'is-active' : ''}`}
                onClick={() => selectColorMode('light')}
                aria-pressed={colorMode === 'light'}
              >
                <i className="fa-solid fa-sun" />
                <span>ライト</span>
              </button>
              <button
                type="button"
                className={`timetable-standalone-mode-option ${colorMode === 'dark' ? 'is-active' : ''}`}
                onClick={() => selectColorMode('dark')}
                aria-pressed={colorMode === 'dark'}
              >
                <i className="fa-solid fa-moon" />
                <span>ダーク</span>
              </button>
            </div>
        </TimetableStandaloneModal>
      ) : null}
      {renderSeatingPreviewModal()}
      {adjustmentModal.open ? (
        <TimetableStandaloneModal
          open={adjustmentModal.open}
          onClose={closeAdjustmentModal}
          title="押し/巻き設定"
          colorMode={colorMode}
          className="timetable-runtime-modal"
        >
            <div className="timetable-runtime-modal-row">
              <span className="timetable-runtime-modal-label">対象</span>
              <span className="timetable-runtime-modal-value">
                {[adjustmentRowTime, adjustmentRowItem].filter(Boolean).join(' ') || '行未指定'}
              </span>
              <span className="timetable-runtime-modal-hint">
                保存した押し/巻きはこの行以降の開始時刻に反映されます。
              </span>
            </div>
            <div className="timetable-runtime-modal-row">
              <span className="timetable-runtime-modal-label">方向</span>
              <div className="timetable-runtime-toggle">
                <button
                  type="button"
                  className={`timetable-runtime-toggle-btn ${
                    adjustmentModal.mode === 'push' ? 'is-active' : ''
                  }`}
                  onClick={() => setAdjustmentModal((prev) => ({ ...prev, mode: 'push' }))}
                >
                  押し
                </button>
                <button
                  type="button"
                  className={`timetable-runtime-toggle-btn ${
                    adjustmentModal.mode === 'pull' ? 'is-active' : ''
                  }`}
                  onClick={() => setAdjustmentModal((prev) => ({ ...prev, mode: 'pull' }))}
                >
                  巻き
                </button>
              </div>
            </div>
            <div className="timetable-runtime-modal-row">
              <label className="timetable-runtime-modal-label" htmlFor="tt-adjust-minutes">
                分数
              </label>
              <input
                id="tt-adjust-minutes"
                type="number"
                min="0"
                step="1"
                className="timetable-runtime-input"
                value={adjustmentModal.minutes}
                onChange={(event) =>
                  setAdjustmentModal((prev) => ({ ...prev, minutes: event.target.value }))
                }
              />
            </div>
            {adjustmentModal.error ? (
              <div className="timetable-runtime-error">{adjustmentModal.error}</div>
            ) : null}
            <div className="timetable-runtime-modal-actions">
              <button
                type="button"
                className="timetable-runtime-action timetable-runtime-action--ghost"
                onClick={handleClearAdjustment}
                disabled={adjustmentModal.saving}
              >
                解除
              </button>
              <button
                type="button"
                className="timetable-runtime-action timetable-runtime-action--primary"
                onClick={handleSaveAdjustment}
                disabled={adjustmentModal.saving}
              >
                {adjustmentModal.saving ? '保存中...' : '保存'}
              </button>
            </div>
        </TimetableStandaloneModal>
      ) : null}
      {memoModal.open ? (
        <TimetableStandaloneModal
          open={memoModal.open}
          onClose={closeMemoModal}
          title="行メモ"
          colorMode={colorMode}
          className="timetable-runtime-modal"
        >
            <div className="timetable-runtime-modal-row">
              <span className="timetable-runtime-modal-label">対象</span>
              <span className="timetable-runtime-modal-value">
                {[memoRowTime, memoRowItem].filter(Boolean).join(' ') || '行未指定'}
              </span>
            </div>
            <div className="timetable-runtime-modal-row">
              <textarea
                className="timetable-runtime-textarea"
                rows={4}
                value={memoModal.content}
                onChange={(event) =>
                  setMemoModal((prev) => ({ ...prev, content: event.target.value }))
                }
                placeholder="メモを入力"
              />
            </div>
            <div className="timetable-runtime-modal-row timetable-runtime-modal-row--inline">
              <span className="timetable-runtime-modal-label">公開範囲</span>
              <label className="timetable-runtime-radio">
                <input
                  type="radio"
                  name="memo-visibility"
                  value="all"
                  checked={memoModal.visibility === 'all'}
                  onChange={() => setMemoModal((prev) => ({ ...prev, visibility: 'all' }))}
                />
                全体
              </label>
              <label className="timetable-runtime-radio">
                <input
                  type="radio"
                  name="memo-visibility"
                  value="admin"
                  checked={memoModal.visibility === 'admin'}
                  onChange={() => setMemoModal((prev) => ({ ...prev, visibility: 'admin' }))}
                />
                管理者のみ
              </label>
            </div>
            <div className="timetable-runtime-modal-row timetable-runtime-modal-row--inline">
              <label className="timetable-runtime-toggle-inline">
                <input
                  type="checkbox"
                  checked={memoModal.sendToChat}
                  onChange={(event) =>
                    setMemoModal((prev) => ({ ...prev, sendToChat: event.target.checked }))
                  }
                />
                <span>イベントチャットにも送信</span>
              </label>
            </div>
            {memoModal.error ? (
              <div className="timetable-runtime-error">{memoModal.error}</div>
            ) : null}
            <div className="timetable-runtime-modal-actions">
              <button
                type="button"
                className="timetable-runtime-action timetable-runtime-action--ghost"
                onClick={handleDeleteMemo}
                disabled={memoModal.saving}
              >
                削除
              </button>
              <button
                type="button"
                className="timetable-runtime-action timetable-runtime-action--primary"
                onClick={handleSaveMemo}
                disabled={memoModal.saving}
              >
                {memoModal.saving ? '保存中...' : '保存'}
              </button>
            </div>
        </TimetableStandaloneModal>
      ) : null}
      {announcementModal.open ? (
        <TimetableStandaloneModal
          open={announcementModal.open}
          onClose={closeAnnouncementModal}
          title="お知らせ追加"
          colorMode={colorMode}
          className="timetable-runtime-modal"
        >
            <div className="timetable-runtime-modal-row">
              <textarea
                className="timetable-runtime-textarea"
                rows={4}
                value={announcementModal.content}
                onChange={(event) =>
                  setAnnouncementModal((prev) => ({ ...prev, content: event.target.value }))
                }
                placeholder="お知らせ内容を入力"
              />
            </div>
            <div className="timetable-runtime-modal-row">
              <label className="timetable-runtime-modal-label" htmlFor="tt-announce-duration">
                表示時間(分)
              </label>
              <input
                id="tt-announce-duration"
                type="number"
                min="1"
                step="1"
                className="timetable-runtime-input"
                value={announcementModal.duration}
                onChange={(event) =>
                  setAnnouncementModal((prev) => ({ ...prev, duration: event.target.value }))
                }
              />
            </div>
            <div className="timetable-runtime-modal-row timetable-runtime-modal-row--inline">
              <span className="timetable-runtime-modal-label">公開範囲</span>
              <label className="timetable-runtime-radio">
                <input
                  type="radio"
                  name="announce-visibility"
                  value="all"
                  checked={announcementModal.visibility === 'all'}
                  onChange={() => setAnnouncementModal((prev) => ({ ...prev, visibility: 'all' }))}
                />
                全体
              </label>
              <label className="timetable-runtime-radio">
                <input
                  type="radio"
                  name="announce-visibility"
                  value="admin"
                  checked={announcementModal.visibility === 'admin'}
                  onChange={() => setAnnouncementModal((prev) => ({ ...prev, visibility: 'admin' }))}
                />
                管理者のみ
              </label>
            </div>
            <div className="timetable-runtime-modal-row timetable-runtime-modal-row--inline">
              <label className="timetable-runtime-toggle-inline">
                <input
                  type="checkbox"
                  checked={announcementModal.sendToChat}
                  onChange={(event) =>
                    setAnnouncementModal((prev) => ({ ...prev, sendToChat: event.target.checked }))
                  }
                />
                <span>イベントチャットにも送信</span>
              </label>
            </div>
            {announcementModal.error ? (
              <div className="timetable-runtime-error">{announcementModal.error}</div>
            ) : null}
            <div className="timetable-runtime-modal-actions">
              <button
                type="button"
                className="timetable-runtime-action timetable-runtime-action--primary"
                onClick={handleSaveAnnouncement}
                disabled={announcementModal.saving}
              >
                {announcementModal.saving ? '保存中...' : '追加'}
              </button>
            </div>
        </TimetableStandaloneModal>
      ) : null}
      {isAnnouncementFullscreen && activeAnnouncement ? (
        <div
          className="timetable-standalone-fullscreen timetable-standalone-fullscreen--announcement"
          role="region"
          aria-label="お知らせ"
          onClick={closeAnnouncementFullscreen}
        >
          <div className="timetable-standalone-fullscreen-content">
            <div className="announcement-fullscreen-label">お知らせ</div>
            {activeAnnouncement.visibility === 'admin' ? (
              <div className="announcement-fullscreen-badge">管理者</div>
            ) : null}
            <div className="announcement-fullscreen-text">{activeAnnouncement.content}</div>
            <div className="clock-hint">タップで閉じる</div>
          </div>
        </div>
      ) : null}
      {isClockFullscreen && !isAnnouncementFullscreen && (
        <div
          className="timetable-standalone-fullscreen"
          role="region"
          aria-label="現在時刻"
          onClick={closeClockFullscreen}
        >
          <div className="timetable-standalone-fullscreen-content">
            <div className="clock-label">現在時刻</div>
            <div className="clock-time">{nowTimeOnly || '—'}</div>
            <div className="clock-program">
              <div
                className={`clock-program-stage ${isProgramAnimating ? 'is-animating' : ''}`}
                style={{ '--tt-program-anim': `${PROGRAM_ANIM_DURATION}ms` }}
              >
                <div className="program-card program-card--current">
                  <div
                    className="program-title"
                    ref={programTitleRef}
                    style={currentTitleFontSize ? { fontSize: `${currentTitleFontSize}px` } : undefined}
                  >
                    {programCurrent?.item || '—'}
                  </div>
                  {renderProgramStartTime(programCurrent)}
                </div>
                {programNext ? (
                  <div className="program-card program-card--next">
                    <div className="program-tag">Next</div>
                    <div className="program-title">{programNext.item || '—'}</div>
                    {renderProgramStartTime(programNext)}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="clock-hint">タップで戻る</div>
          </div>
        </div>
      )}
    </div>
  );
}
